/**
 * Irrigation System Monitoring - Exact Figma Design Match
 * Based on Figma designs:
 * - Basic View: node-id=30360-65049
 * - Advanced View: node-id=30360-65876
 */

const React = require('react');
const { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } =
  React;
const { connect, useDispatch } = require('react-redux');
const { withTranslation } = require('react-i18next');
const PropTypes = require('prop-types');
const { trackPageView } = require('Utils');
const CustomMap = require('CustomMap');
const farmingUnitManager = require('farmingUnitManager');
const {
  isSupportingPlannerByWeeks,
  getFieldItems,
  stress_levels_v2,
} = require('@managers/fieldManager');
const { latestKeyfromDict } = require('@managers/helpers');
const {
  buildFarmingUnitNdviDayIndex,
  getFarmingUnitNdviForTimestamp,
  getNdviDayKey,
  collapseNdviTilesByDay,
} = require('@managers/ndviManager');
const { MiniGraphContainer } = require('../../general/MiniGraph.jsx');
const notificationsManager = require('notificationsManager');
const { getThisWeekIndex } = require('@managers/planningManager');
const { upper } = require('@managers/helpers');
var distributionActions = require('@actions/distributionActions');
const FieldSettingsMenu = require('../FieldSettingsMenu.jsx');
const {
  NotificationItem,
} = require('../../general/NotificationsCenter/NotificationItem.jsx');
var UpdateFieldModal = require('UpdateFieldModal');
var ApprovalModal = require('ApprovalModal');
var ReportIssueModal = require('ReportIssueModal');
var rolesManager = require('@managers/rolesManager');
var $ = require('jquery');

// Custom Hooks
const useIrrigationMonitoring = require('./useIrrigationMonitoring.jsx');
const useIrrigationPersistence = require('./useIrrigationPersistence.jsx');
const useAllFarmViewData = require('./useAllFarmViewData.jsx');
const FieldDetailsWPGraph = require('./FieldDetailsWPGraph.jsx');
const {
  DETAILS_TAB,
  hasValidFieldSelection,
  shouldAutoSelectFirstField,
  shouldRealignAutoSelection,
  isFreshDetailsContext,
} = require('./autoSelectFieldHelpers.js');
const { subscribeToFieldFocus } = require('../../general/fieldFocusBus');
const { resolveFieldFocus } = require('./fieldFocusHelpers.js');
const { buildMapRepaintSignature } = require('./mapRepaintHelpers.js');

// Formatters
const {
  formatRelativeTime,
  formatIrrigationDuration,
  formatIrrigationAmount,
  formatRelativeDay,
  formatTransmitterUpdate,
  formatRecommendation,
  isDurationBasedDisplay,
  isHoursUnit,
  isInchLengthUnit,
  normalizeLengthUnitName,
} = require('../../../utils/allFarmViewFormatters');

function resolveWeeklyPerformanceUnits(apiUnits, userLengthUnit) {
  if (apiUnits && isHoursUnit(apiUnits)) {
    return apiUnits;
  }
  if (apiUnits) {
    return apiUnits;
  }
  return normalizeLengthUnitName(userLengthUnit);
}

// Styles
require('./IrrigationSystemMonitoring.css');

/**
 * Truncates text with ellipsis in the middle if it exceeds maxLength
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum character length before truncating
 * @returns {string} Truncated text with middle ellipsis or original text
 */
const truncateMiddle = (text, maxLength = 45) => {
  if (!text || text.length <= maxLength) return text;

  const ellipsis = '...';
  const charsToShow = maxLength - ellipsis.length;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  return text.slice(0, frontChars) + ellipsis + text.slice(-backChars);
};

// Constants for weekly performance default max ranges
const DEFAULT_MAX_RANGE_MM = 75;
const DEFAULT_MAX_RANGE_INCH = 3;
const DEFAULT_MAX_RANGE_HOURS = 75;
const PLANNED_POSITION_RATIO = 0.75;
const SETTINGS_MENU_ESTIMATED_HEIGHT = 280;
const SETTINGS_MENU_GAP = 4;

/**
 * Blinking Indicator for Active Irrigation
 */
const IrrigationStatusBlinker = () => (
  <div className='irrigation-status-blinker'>
    <div className='indicator' />
  </div>
);

/**
 * Field Row Component - Matches Figma structure
 */
const TOOLTIP_MARGIN = 16;
// Must match .alert-tooltip::before height in IrrigationSystemMonitoring.css
const ARROW_HEIGHT = 24;
// Prevent immediate reopen after click-outside (mousedown closes, click toggles)
const TOOLTIP_REOPEN_DEBOUNCE_MS = 200;

const AlertTooltip = ({ notifications, onClose, anchorRect }) => {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, arrowTop: '50%' });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useLayoutEffect(() => {
    if (!tooltipRef.current || !anchorRect) return;
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const anchorCenter = anchorRect.top + anchorRect.height / 2;
    let idealTop = anchorCenter - tooltipHeight / 2;

    // Clamp to viewport
    const minTop = TOOLTIP_MARGIN;
    const maxTop = window.innerHeight - tooltipHeight - TOOLTIP_MARGIN;
    const clampedTop = Math.max(minTop, Math.min(idealTop, maxTop));

    // Arrow points at anchor center relative to clamped tooltip position
    const arrowTop = Math.max(
      ARROW_HEIGHT,
      Math.min(anchorCenter - clampedTop, tooltipHeight - ARROW_HEIGHT),
    );

    setPosition({ top: clampedTop, arrowTop: `${arrowTop}px` });
  }, [anchorRect]);

  const sortedNotifications = useMemo(
    () => [...notifications].sort((a, b) => b.date - a.date),
    [notifications],
  );

  return (
    <div
      className='alert-tooltip'
      ref={tooltipRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        top: position.top,
        left: anchorRect ? anchorRect.right + 6 : 0,
        '--arrow-top': position.arrowTop,
      }}
    >
      <div className='alert-tooltip-list'>
        {sortedNotifications.map((notification) => (
          <NotificationItem
            key={
              notification.id || `${notification.field_id}-${notification.date}`
            }
            notificationData={notification}
            onCloseClicked={onClose}
            clickable={false}
          />
        ))}
      </div>
    </div>
  );
};

AlertTooltip.propTypes = {
  notifications: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorRect: PropTypes.object,
};

const FieldRow = ({
  field,
  onRowClick,
  onAlertClick,
  isSelected,
  fieldNotifications,
  isAdvanced,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  settingsMenu,
  isMenuOpen,
  onMenuToggle,
  menuPosition,
  userData,
  t,
}) => {
  const {
    name,
    cropType,
    waterAvailability,
    waterAvailabilityLevel, // 'low', 'medium', 'high'
    supportsWeeklyPlanner,
    units,
    weeklyRecommendation,
    weeklyPerformance,
    irrigationActivity,
    irrigationSystem,
    irrigationRate,
    lastIrrigationAmount,
    lastIrrigationAmountUnit,
    wpSamples,
    timeZone,
  } = field;

  const [showAlertTooltip, setShowAlertTooltip] = useState(false);
  const triggerAreaRef = useRef(null);
  const tooltipClosedAtRef = useRef(0);

  // Transform wpSamples to graph data format (same as FieldsSelector)
  var samples;
  if (wpSamples !== undefined) {
    samples = wpSamples.map(function (sample, index) {
      return {
        value: parseFloat(sample.value),
        date: sample.date,
        index: index,
      };
    });
  }

  const hasAlert = fieldNotifications && fieldNotifications.length > 0;

  const handleAlertClick = (e) => {
    e.stopPropagation();
    if (!hasAlert) return;
    // Skip toggle if tooltip was just closed by click-outside (mousedown)
    if (Date.now() - tooltipClosedAtRef.current < TOOLTIP_REOPEN_DEBOUNCE_MS)
      return;
    setShowAlertTooltip((prev) => !prev);
    if (onAlertClick) {
      onAlertClick(fieldNotifications);
    }
  };

  const handleAlertTooltipClose = useCallback(() => {
    tooltipClosedAtRef.current = Date.now();
    setShowAlertTooltip(false);
  }, []);

  // Determine status color
  const getStatusColor = () => {
    switch (waterAvailabilityLevel?.toLowerCase()) {
      case 'low':
        return '#DF3044';
      case 'medium':
        return '#FFB302';
      case 'high':
        return '#078378';
      default:
        return '#DCE0E8';
    }
  };

  const getStatusTextColor = () => {
    switch (waterAvailabilityLevel?.toLowerCase()) {
      case 'low':
      case 'high':
        return '#FFFFFF';
      case 'medium':
        return '#2A2F40';
      default:
        return '#3B4259';
    }
  };

  // Follows the field's "Show irrigation data in" setting, falling back to the
  // irrigation type default — same rule the API uses for the other values.
  const showIrrigationDuration = isDurationBasedDisplay(irrigationSystem);

  const irrigationDetailElement =
    irrigationActivity?.lastStartedAt && irrigationActivity?.lastEndedAt
      ? showIrrigationDuration
        ? formatIrrigationDuration(
            irrigationActivity.lastStartedAt,
            irrigationActivity.lastEndedAt,
            t,
          )
        : formatIrrigationAmount(
            lastIrrigationAmount,
            lastIrrigationAmountUnit || userData?.length || 'mm',
          )
      : null;

  return (
    <div
      className={`field-row ${isSelected ? 'selected' : ''} ${isAdvanced ? 'advanced-mode' : 'basic-mode'}`}
      data-field-id={field.id}
      onClick={() => onRowClick(field)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Alert trigger area */}
      <div
        className='trigger-area'
        ref={triggerAreaRef}
        onClick={handleAlertClick}
      >
        {hasAlert && (
          <img
            src={require('images/icons/field_alert_icon.svg')}
            width='16'
            height='16'
            alt='alert'
            className='alert-icon'
            style={{ cursor: 'pointer' }}
          />
        )}
        {showAlertTooltip && hasAlert && (
          <AlertTooltip
            notifications={fieldNotifications}
            onClose={handleAlertTooltipClose}
            anchorRect={triggerAreaRef.current?.getBoundingClientRect()}
          />
        )}
      </div>

      {/* Main content */}
      <div className='main-content'>
        {/* BASIC section - always visible */}
        <div className='basic-section'>
          {/* Field name & Crop type */}
          <div className='name-and-crop'>
            <div className='field-name' title={name}>
              {truncateMiddle(name, 45)}
            </div>
            <div className='crop-type'>{upper(cropType)}</div>
          </div>

          {/* Crop water availability status */}
          <div className='status-wrap'>
            {waterAvailabilityLevel && waterAvailabilityLevel !== 'none' && (
              <div
                className='crop-water-availability-status'
                style={{
                  backgroundColor: getStatusColor(),
                  color: getStatusTextColor(),
                }}
              >
                {waterAvailability}
              </div>
            )}
          </div>
        </div>

        {/* ADVANCED section - always rendered, hidden via CSS when not in advanced mode */}
        <div className='advanced-section' aria-hidden={!isAdvanced}>
          {/* Weekly recommendation */}
          <div className='cell recommendation-cell'>
            {!supportsWeeklyPlanner ? (
              <span className='not-available-text'>{t('not_available')}</span>
            ) : (
              (() => {
                const { amountValue, amountUnit, frequencyText } =
                  formatRecommendation(
                    weeklyRecommendation?.amount,
                    weeklyRecommendation?.unit,
                    weeklyRecommendation?.frequencyDays,
                    weeklyRecommendation?.timesPerWeek,
                    weeklyRecommendation?.mode,
                    t,
                  );
                return amountValue ? (
                  <div className='recommendation-amount'>
                    {amountUnit ? (
                      <span className='value-with-unit'>
                        <span className='value'>{amountValue}</span>
                        <span
                          className={
                            amountUnit === '\u2033'
                              ? 'unit-wrap unit-wrap--inch'
                              : 'unit-wrap'
                          }
                        >
                          <span
                            className={
                              amountUnit === '\u2033'
                                ? 'unit unit--inch'
                                : 'unit'
                            }
                          >
                            {amountUnit}
                          </span>
                        </span>
                      </span>
                    ) : (
                      <span className='value'>{amountValue}</span>
                    )}
                    {frequencyText && (
                      <>
                        <br />
                        <span className='frequency'>{frequencyText}</span>
                      </>
                    )}
                  </div>
                ) : (
                  <div>—</div>
                );
              })()
            )}
          </div>

          {/* Weekly performance with bar */}
          <div className='cell performance-cell'>
            {(() => {
              // Determine if we have a valid goal (non-zero)
              var hasValidGoal =
                weeklyPerformance &&
                weeklyPerformance.goal !== null &&
                weeklyPerformance.goal > 0;

              const performanceUnits = resolveWeeklyPerformanceUnits(
                field.weeklyPerformanceUnits,
                userData?.length,
              );
              const isHours = isHoursUnit(performanceUnits);
              const isImperial = isInchLengthUnit(performanceUnits);

              // Default max range based on unit type
              const defaultMaxRange = isHours
                ? DEFAULT_MAX_RANGE_HOURS
                : isImperial
                  ? DEFAULT_MAX_RANGE_INCH
                  : DEFAULT_MAX_RANGE_MM;

              // Resolve the unit symbol for display
              const unitSymbol = isHours ? 'h' : isImperial ? '\u2033' : 'mm';
              const isInchUnit = unitSymbol === '\u2033';
              const unitWrapClassName = isInchUnit
                ? 'unit-wrap unit-wrap--inch'
                : 'unit-wrap';
              const unitClassName = isInchUnit ? 'unit unit--inch' : 'unit';

              const hasValue =
                weeklyPerformance &&
                weeklyPerformance.value !== null &&
                weeklyPerformance.value !== undefined;

              const displayGoalValue = hasValidGoal
                ? weeklyPerformance.goal
                : null;
              const displayPerformanceValue = hasValue
                ? weeklyPerformance.value
                : null;

              // Total bar represents planned at 75%, so total = goal / PLANNED_POSITION_RATIO
              const maxRange =
                hasValidGoal && displayGoalValue > 0
                  ? displayGoalValue / PLANNED_POSITION_RATIO
                  : defaultMaxRange;

              // Format value: round to nearest 0.5 for hours, 2 decimals for inch, 1 for mm
              const formatVal = (v) =>
                isHours
                  ? Math.round(v * 2) / 2
                  : parseFloat(v.toFixed(isImperial ? 2 : 1));

              const railPercent = hasValue
                ? (displayPerformanceValue / maxRange) * 100
                : 0;
              const isOverTarget = !hasValidGoal && railPercent > 100;
              const railFillClass =
                'actual-indicator' +
                (isOverTarget ? ' actual-indicator--over-target' : '');

              return (
                <div className='performance-indicator'>
                  {/* Actual value with unit */}
                  <div className='actual-value'>
                    {hasValue ? (
                      <span className='value-with-unit'>
                        <span className='value'>
                          {formatVal(displayPerformanceValue)}
                        </span>
                        <span className={unitWrapClassName}>
                          <span className={unitClassName}>{unitSymbol}</span>
                        </span>
                      </span>
                    ) : (
                      <span className='value-with-unit'>
                        <span className='value'>0</span>
                        <span className={unitWrapClassName}>
                          <span className={unitClassName}>{unitSymbol}</span>
                        </span>
                      </span>
                    )}
                  </div>

                  {/* Progress bar rail - always show */}
                  <div className='rail'>
                    <div
                      className={railFillClass}
                      style={{
                        width: Math.min(railPercent, 100) + '%',
                      }}
                    />
                  </div>

                  {/* Goal value indicator at 75% - only show if goal > 0 */}
                  {hasValidGoal && (
                    <div className='planned-wrap'>
                      <div className='planned-value'>
                        <span className='value-with-unit'>
                          <span className='value'>
                            {formatVal(displayGoalValue)}
                          </span>
                          <span className={unitWrapClassName}>
                            <span className={unitClassName}>{unitSymbol}</span>
                          </span>
                        </span>
                      </div>
                      <div className='planned-indicator' />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Last irrigation */}
          <div className='cell irrigation-cell'>
            <div className='irrigation-content'>
              {irrigationActivity && irrigationActivity.isActive ? (
                <>
                  {/* Active irrigation - show start time with relative day */}
                  <div className='irrigation-time'>
                    {irrigationActivity.lastStartedAt ? (
                      formatRelativeDay(irrigationActivity.lastStartedAt, t)
                    ) : (
                      <span className='not-available-text'>
                        {t('not_available')}
                      </span>
                    )}
                  </div>
                  <div className='active-irrigation-indicator'>
                    <IrrigationStatusBlinker />
                    <span>{t('irrigating')}</span>
                  </div>
                </>
              ) : (
                <>
                  {/* No active irrigation - show last irrigation info */}
                  <div className='irrigation-time'>
                    {irrigationActivity && irrigationActivity.lastEndedAt ? (
                      formatRelativeTime(irrigationActivity.lastEndedAt, t)
                    ) : (
                      <span className='not-available-text'>
                        {t('not_available')}
                      </span>
                    )}
                  </div>
                  {/* Duration or amount of last irrigation */}
                  {irrigationDetailElement && (
                    <div className='irrigation-duration'>
                      ({irrigationDetailElement})
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Only show clock icon if we have irrigation data */}
            {irrigationActivity &&
              (irrigationActivity.isActive ||
                irrigationActivity.lastEndedAt) && (
                <div className='clock-icon-wrapper'>
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 14 14'
                    fill='none'
                    className='time-icon'
                  >
                    <circle
                      cx='7'
                      cy='7'
                      r='6'
                      stroke='#626E84'
                      strokeWidth='1'
                    />
                    <path
                      d='M7 3.5V7L9.5 9'
                      stroke='#626E84'
                      strokeWidth='1'
                      strokeLinecap='round'
                    />
                  </svg>
                  <div className='clock-tooltip'>
                    {irrigationActivity.lastTransmitterUpdate
                      ? formatTransmitterUpdate(
                          irrigationActivity.lastTransmitterUpdate,
                          t,
                        )
                      : t('transmitter_data_missing')}
                  </div>
                </div>
              )}
          </div>

          {/* Roots water potential (Cbar) */}
          <div className='cell cbar-cell'>
            <div className='cbar-content'>
              {samples && samples.length > 0 ? (
                <MiniGraphContainer data={samples} timeZone={timeZone} />
              ) : (
                <div className='cbar-no-data'>
                  <span className='not-available-text'>
                    {t('not_available')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Settings menu on hover */}
      {(isHovered || isMenuOpen) && settingsMenu && (
        <div className='field-row-settings'>
          <div className='field-row-settings__gradient' />
          <div className={`dropdown ${isMenuOpen ? 'show' : ''}`}>
            <div
              className='field-row-settings__icon'
              aria-haspopup='true'
              aria-expanded={isMenuOpen}
              onClick={(e) => {
                e.stopPropagation();
                onMenuToggle(!isMenuOpen, e.currentTarget);
              }}
            >
              <svg width='4' height='16' viewBox='0 0 4 16' fill='#4D5674'>
                <circle cx='2' cy='2' r='2' />
                <circle cx='2' cy='8' r='2' />
                <circle cx='2' cy='14' r='2' />
              </svg>
            </div>
            {isMenuOpen && menuPosition && (
              <div
                className='field-row-settings__menu-portal'
                style={{
                  '--menu-top': `${menuPosition.top}px`,
                  '--menu-left': `${menuPosition.left}px`,
                }}
              >
                {settingsMenu}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

FieldRow.propTypes = {
  field: PropTypes.object.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onAlertClick: PropTypes.func,
  isSelected: PropTypes.bool,
  fieldNotifications: PropTypes.array,
  isAdvanced: PropTypes.bool,
  isHovered: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  settingsMenu: PropTypes.node,
  isMenuOpen: PropTypes.bool,
  onMenuToggle: PropTypes.func,
  menuPosition: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
  }),
  t: PropTypes.func.isRequired,
};

/**
 * Table Header Component
 */
const TableHeader = ({ isAdvanced, t }) => (
  <div className={`table-header ${isAdvanced ? 'advanced' : 'basic'}`}>
    {/* Basic columns */}
    <div className='basic-columns'>
      <div className='header-cell field-name-header'>
        <span>
          {t('field_name')}
          <br />& {t('crop_type')}
        </span>
      </div>
      <div className='header-cell status-header'>
        <span>{t('gauge_title_v3')}</span>
      </div>
    </div>

    {/* Advanced columns - always rendered, hidden via CSS when not in advanced mode */}
    <div className='advanced-columns' aria-hidden={!isAdvanced}>
      <div className='header-cell recommendation-header'>
        <span>{t('edit_field_planner_type_by_week')}</span>
      </div>
      <div className='header-cell performance-header'>
        <span className='performance-title'>{t('weekly_performance')}</span>
        <div className='info-icon-wrapper'>
          <img
            src={require('images/icons/info_icon.svg')}
            className='info-icon'
          />
          <div className='info-tooltip'>{t('weekly_performance_tooltip')}</div>
        </div>
      </div>
      <div className='header-cell irrigation-header'>
        <span>{t('irrigation_activity')}</span>
        <div className='info-icon-wrapper'>
          <img
            src={require('images/icons/info_icon.svg')}
            className='info-icon'
          />
          <div className='info-tooltip'>{t('irrigation_activity_tooltip')}</div>
        </div>
      </div>
      <div className='header-cell cbar-header'>
        <span>{t('water_potential')}</span>
        <div className='info-icon-wrapper'>
          <img
            src={require('images/icons/info_icon.svg')}
            className='info-icon'
          />
          <div className='info-tooltip'>
            {t('root_zone_water_potential_tooltip')}
          </div>
        </div>
      </div>
    </div>
  </div>
);

TableHeader.propTypes = {
  isAdvanced: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

/**
 * Main Component
 */
const IrrigationSystemMonitoring = ({
  selectedFarmingUnit,
  selectedDistribution,
  selectedField,
  plannerType,
  archiveMode,
  userNotifications,
  userDetails,
  userData,
  farmingUnitRecommendations,
  loadHistoryStatus,
  fieldLoadHistoryStatus,
  distributions,
  loadFarmingUnitHistory,
  loadFieldHistory,
  deleteField,
  updateField,
  configurationData,
  farmingUnitRoles,
  fieldActions,
  t,
}) => {
  const dispatch = useDispatch();
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  // A "focus this field" request from the global search, applied once the
  // field's farming unit is the one on screen.
  const [pendingFieldFocus, setPendingFieldFocus] = useState(null);
  const [customMapInstance, setCustomMapInstance] = useState(null);
  const [showNDVI, setShowNDVI] = useState(false);
  // Trailing-panel view tab: 'farm' = the map, 'details' = field details
  // (water-potential graph will be wired in once the layout is approved).
  const [trailingTab, setTrailingTab] = useState('farm');
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [sortOption, setSortOption] = useState('crop_availability_low_high');
  const [hoveredFieldId, setHoveredFieldId] = useState(null);
  const [openMenuFieldId, setOpenMenuFieldId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  // State to force re-render when historical_data (wp_samples) is loaded
  const [historyStatusUpdatedAt, setHistoryStatusUpdatedAt] = useState(null);

  const sortButtonRef = useRef(null);
  const sortMenuRef = useRef(null);
  // Track previous loadHistoryStatus to detect changes
  const prevLoadHistoryStatusRef = useRef(null);
  // Refs for Field Details auto-selection: detect entering the Details tab /
  // switching farming unit, and remember a deliberate deselect so we don't
  // immediately re-select the field the user just cleared.
  const prevTrailingTabRef = useRef(trailingTab);
  const prevFarmingUnitIdRef = useRef(null);
  const suppressAutoSelectRef = useRef(false);
  // True while the current selection came from auto-select (not the user), so
  // it may follow the sorted-first field as the sort settles/changes.
  const autoSelectedRef = useRef(false);

  // Fetch real data from API with auto-refresh
  var farmingUnitId = selectedFarmingUnit && selectedFarmingUnit.id;
  var allFarmViewData = useAllFarmViewData(
    farmingUnitId,
    userData && userData.length,
  );

  // Saving a field changes what the farm view shows — weekly performance and
  // goal can switch units (mm ↔ hours) or values. The recommendations come back
  // via Redux, but this data is component-local and would otherwise sit stale
  // until the next auto-refresh tick, so re-pull it on every successful field
  // create/update/delete.
  const refreshAllFarmViewData = allFarmViewData.refresh;
  const lastHandledFieldActionRef = useRef(null);
  useEffect(() => {
    const actionType = fieldActions && fieldActions.type;
    const actionStatus = fieldActions && fieldActions.status;
    if (actionStatus !== 'success' || !actionType) {
      // Cleared / failed action — let the next success through.
      lastHandledFieldActionRef.current = null;
      return;
    }
    const signature = `${actionType}:${actionStatus}`;
    if (lastHandledFieldActionRef.current === signature) {
      return;
    }
    lastHandledFieldActionRef.current = signature;
    refreshAllFarmViewData();
  }, [fieldActions, refreshAllFarmViewData]);

  // Get notifications for fields using notificationsManager
  const getFieldNotifications = useCallback(() => {
    if (!userNotifications || Object.keys(userNotifications).length === 0) {
      return { allNotifications: [], fieldNotificationsMap: {} };
    }

    const { allNotifications } = notificationsManager.notificationsDataMap(
      userNotifications,
      null,
    );

    // Group notifications by field_id
    const fieldNotificationsMap = {};
    allNotifications.forEach((notification) => {
      const fieldId = notification.field_id;
      if (fieldId) {
        if (!fieldNotificationsMap[fieldId]) {
          fieldNotificationsMap[fieldId] = [];
        }
        fieldNotificationsMap[fieldId].push(notification);
      }
    });

    return { allNotifications, fieldNotificationsMap };
  }, [userNotifications]);

  const { fieldNotificationsMap } = getFieldNotifications();

  // Track analytics
  const trackEvent = useCallback((eventName, properties) => {
    if (typeof mixpanel !== 'undefined' && mixpanel && mixpanel.track) {
      mixpanel.track(eventName, properties);
    }
  }, []);

  // Track page view on mount
  useEffect(() => {
    trackPageView('All Farm');
  }, []);

  // Load farming unit history (wp_samples) on mount so crop water availability data is available
  useEffect(
    function () {
      if (
        selectedDistribution &&
        selectedFarmingUnit &&
        loadFarmingUnitHistory
      ) {
        loadFarmingUnitHistory(
          selectedDistribution.id,
          selectedFarmingUnit.id,
          null,
        );
      }
    },
    [
      selectedDistribution && selectedDistribution.id,
      selectedFarmingUnit && selectedFarmingUnit.id,
    ],
  );

  // Force re-render when historical_data is loaded (farming unit or field).
  // Redux mutates nested objects in place; status transitions are the reliable signal.
  const prevFieldLoadHistoryStatusRef = useRef(null);
  useEffect(
    function () {
      var prevFuStatus =
        prevLoadHistoryStatusRef.current &&
        prevLoadHistoryStatusRef.current.status;
      var currentFuStatus = loadHistoryStatus && loadHistoryStatus.status;
      var prevFieldStatus =
        prevFieldLoadHistoryStatusRef.current &&
        prevFieldLoadHistoryStatusRef.current.status;
      var currentFieldStatus =
        fieldLoadHistoryStatus && fieldLoadHistoryStatus.status;

      if (
        prevFuStatus !== currentFuStatus ||
        prevFieldStatus !== currentFieldStatus
      ) {
        setHistoryStatusUpdatedAt(Date.now());
      }

      prevLoadHistoryStatusRef.current = loadHistoryStatus;
      prevFieldLoadHistoryStatusRef.current = fieldLoadHistoryStatus;
    },
    [loadHistoryStatus, fieldLoadHistoryStatus],
  );

  // Fresh farming unit from distributions (authoritative), matching mobile
  // DashboardMap and web FieldsSelector — selected_entities can lag behind.
  const farmingUnitForMap = useMemo(
    function () {
      var distId = selectedDistribution && selectedDistribution.id;
      var fuId = selectedFarmingUnit && selectedFarmingUnit.id;
      if (
        distId &&
        fuId &&
        distributions &&
        distributions[distId] &&
        distributions[distId].farming_units &&
        distributions[distId].farming_units[fuId]
      ) {
        return distributions[distId].farming_units[fuId];
      }
      return selectedFarmingUnit;
    },
    [
      distributions,
      selectedDistribution,
      selectedFarmingUnit,
      selectedFieldId,
      fieldLoadHistoryStatus && fieldLoadHistoryStatus.status,
      historyStatusUpdatedAt,
    ],
  );

  // Invalidate map size when user returns to the tab (fixes map being cut off)
  useEffect(
    function () {
      function handleVisibilityChange() {
        if (
          document.visibilityState === 'visible' &&
          customMapInstance &&
          customMapInstance.mymap
        ) {
          // Small delay to ensure DOM has updated
          setTimeout(function () {
            customMapInstance.mymap.invalidateSize();
          }, 100);
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return function () {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      };
    },
    [customMapInstance],
  );

  // Handle toggle advanced mode
  const handleAdvancedToggle = useCallback(() => {
    setIsAdvancedMode((prev) => {
      const newMode = !prev;
      trackEvent('Farm View Advanced Toggled', { enabled: newMode });
      return newMode;
    });

    // Invalidate map size after CSS transition completes (350ms)
    // This fixes the map being cut off when switching back from another browser tab
    if (customMapInstance && customMapInstance.mymap) {
      setTimeout(function () {
        customMapInstance.mymap.invalidateSize();
      }, 400);
    }
  }, [trackEvent, customMapInstance]);

  // Close field settings menu when clicking outside
  useEffect(
    function () {
      if (!openMenuFieldId) return;

      function handleClickOutside(e) {
        if (!e.target.closest('.field-row-settings')) {
          setOpenMenuFieldId(null);
          setMenuPosition(null);
        }
      }

      // Delay adding listener to avoid catching the opening click
      var listenerAdded = false;
      var timeoutId = setTimeout(function () {
        document.addEventListener('click', handleClickOutside);
        listenerAdded = true;
      }, 0);

      return function () {
        clearTimeout(timeoutId);
        if (listenerAdded) {
          document.removeEventListener('click', handleClickOutside);
        }
      };
    },
    [openMenuFieldId],
  );

  // Invalidate map size when user returns to the tab (fixes map being cut off)
  useEffect(
    function () {
      function handleVisibilityChange() {
        if (
          document.visibilityState === 'visible' &&
          customMapInstance &&
          customMapInstance.mymap
        ) {
          setTimeout(function () {
            customMapInstance.mymap.invalidateSize();
          }, 100);
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return function () {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      };
    },
    [customMapInstance],
  );

  // Select a field: update local state, sync redux (so the WP graph and any
  // other consumer of selected_entities.field follow), and highlight it on
  // the map (which reads selectedFieldId). `source` is 'list' | 'map' |
  // 'keyboard' | 'auto' (analytics + auto-follow behavior). Shared by the
  // list, the map, keyboard nav, and the auto-select effect.
  const selectField = (field, source) => {
    setSelectedFieldId(field.id);
    // An auto-selection may later follow the sorted-first field; a manual
    // selection pins the user's choice and stops the auto-follow.
    autoSelectedRef.current = source === 'auto';
    const fullField = selectedFarmingUnit?.fields?.[field.id] || field;
    dispatch(
      distributionActions.selected_entities(
        selectedDistribution,
        selectedFarmingUnit,
        fullField,
        null,
      ),
    );
    trackEvent('Farm View Field Item Interaction', {
      field_id: field.id,
      action: 'selected',
      source,
    });
    trackEvent('Field Change', {
      'Field ID': field.id,
      'Field Name': fullField?.name,
      Source: 'Farm View',
      Component: source === 'map' ? 'Map' : source === 'auto' ? 'Auto' : 'List',
    });
  };

  // Deselect the current field. `userInitiated` records a deliberate deselect
  // so the auto-select won't immediately re-pick the field.
  const deselectField = (fieldId, source, userInitiated) => {
    setSelectedFieldId(null);
    autoSelectedRef.current = false;
    if (userInitiated) {
      suppressAutoSelectRef.current = true;
    }
    // Clear redux selected field so the WP graph goes back to the empty state.
    dispatch(
      distributionActions.selected_entities(
        selectedDistribution,
        selectedFarmingUnit,
        null,
        null,
      ),
    );
    trackEvent('Farm View Field Item Interaction', {
      field_id: fieldId,
      action: 'unselected',
      source,
    });
  };

  // Handle field selection from the list (toggle behavior)
  const handleFieldSelection = (field) => {
    if (selectedFieldId === field.id) {
      deselectField(field.id, 'list', true);
    } else {
      selectField(field, 'list');
    }
  };

  // The global search can point at a field in another farming unit, so a focus
  // request is parked until the unit it belongs to is the one on screen.
  // subscribeToFieldFocus returns its own unsubscribe, used here as the effect
  // cleanup, so a request can never set state on an unmounted Farm View.
  useEffect(() => {
    const unsubscribe = subscribeToFieldFocus(function (focus) {
      setPendingFieldFocus(focus);
    });
    return unsubscribe;
  }, []);

  // Applies a parked focus request. This effect sets state, which is safe here
  // because it converges: resolveFieldFocus clears the request in the same pass
  // that resolves it, and a cleared request makes the next pass a no-op — so
  // the effect re-runs at most once more, never in a loop. Pinned by
  // __tests__/fieldFocusHelpers.test.js.
  useEffect(() => {
    const { field, shouldClear } = resolveFieldFocus(
      pendingFieldFocus,
      farmingUnitId,
      selectedFarmingUnit?.fields,
    );

    if (field) {
      // Batched with the selection so the auto-select effect sees a valid
      // selection on the Details tab and stays out of the way.
      if (pendingFieldFocus.openDetails) {
        setTrailingTab(DETAILS_TAB);
      }
      selectField(field, 'search');
    }
    if (shouldClear) {
      setPendingFieldFocus(null);
    }
    // selectField is recreated each render; the guards above keep this to a
    // single application per focus request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFieldFocus, farmingUnitId, selectedFarmingUnit]);

  // Load field history (including NDVI) when a field is selected
  useEffect(() => {
    if (
      selectedFieldId &&
      selectedDistribution?.id &&
      selectedFarmingUnit?.id
    ) {
      const field = selectedFarmingUnit.fields?.[selectedFieldId];
      const hasNdvi = field?.historical_data?.ndvi;
      if (!hasNdvi) {
        loadFieldHistory(
          selectedDistribution.id,
          selectedFarmingUnit.id,
          selectedFieldId,
        );
      }
    }
  }, [
    selectedFieldId,
    selectedDistribution?.id,
    selectedFarmingUnit?.id,
    loadFieldHistory,
  ]);

  // Get user role
  const isAdmin = selectedFarmingUnit?.role === 'admin';
  const isModerator = selectedFarmingUnit?.role === 'moderator';

  // Close field settings menu
  const closeFieldSettingsMenu = () => {
    setOpenMenuFieldId(null);
    setMenuPosition(null);
  };

  // Check if user should see report issue option
  const isNdripUser = rolesManager.isNdripUser(userDetails?.email);
  const showReportIssue = isNdripUser;

  // Generate settings menu for a field (only for admin/moderator)
  const renderFieldSettingsMenu = (field) => {
    if (!isAdmin && !isModerator) return null;

    return (
      <FieldSettingsMenu
        field={field}
        isAdmin={isAdmin}
        isModerator={isModerator}
        showReportIssue={showReportIssue}
        distributionId={selectedDistribution?.id}
        distributionName={selectedDistribution?.name}
        farmingUnitId={selectedFarmingUnit?.id}
        farmingUnitName={selectedFarmingUnit?.name}
        userName={
          userDetails?.first_name || userDetails?.last_name
            ? `${userDetails?.first_name ?? ''} ${userDetails?.last_name ?? ''}`.trim()
            : undefined
        }
        userEmail={userDetails?.email}
        farmingUnitUserEmails={
          farmingUnitRoles?.roles ? Object.keys(farmingUnitRoles.roles) : []
        }
        onClose={closeFieldSettingsMenu}
        trackEvent={trackEvent}
        t={t}
        className='field-settings-menu'
      />
    );
  };

  // Handle map click (toggle behavior)
  const handlePolygonClick = (fieldId) => {
    if (selectedFieldId === fieldId) {
      deselectField(fieldId, 'map', true);
    } else {
      const field = selectedFarmingUnit?.fields?.[fieldId] || { id: fieldId };
      selectField(field, 'map');
    }
  };

  const handleMarkerClick = () => {};

  // Handle map layer selection (NDVI toggle sync)
  const handleLayerSelection = useCallback(
    (layerId) => {
      const ndviOn = layerId === 'ndvi';
      setShowNDVI(ndviOn);
      trackEvent('Farm View NDVI Toggled', { enabled: ndviOn });
    },
    [trackEvent],
  );

  // Prepare fields data
  const prepareFieldsData = () => {
    if (!selectedFarmingUnit || !selectedFarmingUnit.fields) {
      return [];
    }

    const fieldsObj = selectedFarmingUnit.fields;
    const fieldsState = archiveMode ? 'archive' : 'active';

    // Use Object.entries to preserve the key (which is used for area mapping)
    const fieldsEntries = Array.isArray(fieldsObj)
      ? fieldsObj.map((f, i) => [f.id || i, f])
      : Object.entries(fieldsObj);

    // Filter to only include active fields with geo_data (same as all_farming_unit_areas)
    const filteredEntries = fieldsEntries.filter(function ([fieldKey, field]) {
      var fieldState = field.settings && field.settings.field_state;
      var hasAreas = field.geo_data && field.geo_data.areas;
      return fieldState === fieldsState && hasAreas;
    });

    return filteredEntries.map(([fieldKey, field]) => {
      // Use the fieldKey (object key) as ID since that's what areas use
      const fieldId = fieldKey;

      // Get real stress level from field data (same as FieldsSelector / Field View)
      var fieldItems = getFieldItems(field);
      var stressValue = fieldItems.stressDataItem.value;
      // Crop water availability is shown for every planner state, so the v2
      // bands apply to all fields. This used to resolve the band per field
      // (v1 for daily / no planner, v2 for weekly) to support farming units
      // with a mix of planner types — that distinction no longer exists.
      var stressLevel = 'none';
      for (var key in stress_levels_v2) {
        var record = stress_levels_v2[key];
        if (stressValue >= record.min_value && stressValue < record.max_value) {
          if (record.group === 1) {
            stressLevel = 'low';
          } else if (record.group === 2) {
            stressLevel = 'medium';
          } else {
            stressLevel = 'high';
          }
          break;
        }
      }

      const stressLevelDisplay =
        stressLevel === 'none' ? '' : t(`water_availability_${stressLevel}`);

      // Get recommendation data from Redux (farmingUnitRecommendations)
      var fieldRecommendations = farmingUnitRecommendations[fieldId];
      var recArr =
        fieldRecommendations && fieldRecommendations.recommendations
          ? fieldRecommendations.recommendations
          : [];
      var fieldUnits =
        field.settings && field.settings.units ? field.settings.units : 'mm';
      var recUnits =
        fieldRecommendations && fieldRecommendations.units
          ? fieldRecommendations.units
          : fieldUnits;

      // Find this week's recommendation using getThisWeekIndex
      var irrigations = {};
      if (recArr.length > 0) {
        var weekKeys = recArr
          .map(function (rec) {
            return rec && rec.local_midnight_week_start;
          })
          .filter(Boolean)
          .sort();
        // The API returns weeks as [previous, current, next], so 'this week' is
        // the middle element — see getThisWeekIndex.
        var thisWeekIdx = getThisWeekIndex(weekKeys);
        var thisWeekKey = weekKeys[thisWeekIdx];
        var thisWeekRec = thisWeekKey
          ? recArr.find(function (rec) {
              return rec && rec.local_midnight_week_start === thisWeekKey;
            })
          : null;
        irrigations =
          thisWeekRec && thisWeekRec.irrigations ? thisWeekRec.irrigations : {};
      }

      const recAmount = irrigations.amount || 0;
      const recUnit = irrigations.value_units || recUnits;
      const recIntervalDays = irrigations.events || 0;

      // Get real data from API if available
      var apiFieldData = null;
      if (allFarmViewData.data && allFarmViewData.data.fields) {
        apiFieldData = allFarmViewData.data.fields.find(function (f) {
          return f.fieldId === fieldId;
        });
      }

      // Build weeklyPerformance - use real data if available, otherwise null (shows N/A)
      var weeklyPerformance;
      if (apiFieldData && apiFieldData.weeklyPerformance !== undefined) {
        weeklyPerformance = {
          value: apiFieldData.weeklyPerformance,
          goal: apiFieldData.weeklyGoal,
        };
      } else {
        // No data - will show N/A
        weeklyPerformance = {
          value: null,
          goal: null,
        };
      }

      // Build irrigationActivity - use real data if available, otherwise null (shows N/A)
      // Treat timestamps before year 2000 as missing (API may return epoch-0 defaults)
      var validTimestamp = function (ts) {
        return ts && new Date(ts).getFullYear() >= 2000 ? ts : null;
      };
      var irrigationActivity;
      if (apiFieldData) {
        irrigationActivity = {
          lastStartedAt: validTimestamp(apiFieldData.lastIrrigationStartedAt),
          lastEndedAt: validTimestamp(apiFieldData.lastIrrigationEndedAt),
          source: apiFieldData.lastIrrigationSource,
          isActive: apiFieldData.isIrrigating || false,
          lastTransmitterUpdate: validTimestamp(
            apiFieldData.lastTransmitterUpdate,
          ),
        };
      } else {
        // No data - will show N/A
        irrigationActivity = {
          lastStartedAt: null,
          lastEndedAt: null,
          isActive: false,
          lastTransmitterUpdate: null,
        };
      }

      var cropType =
        field.crop_data && field.crop_data.crop_type
          ? field.crop_data.crop_type
          : t('not_available');

      var plannerMode =
        field.settings &&
        field.settings.planner_settings &&
        field.settings.planner_settings.mode
          ? field.settings.planner_settings.mode
          : 'weekly';

      var supportsWeeklyPlanner = isSupportingPlannerByWeeks(field);

      // Get water potential data from field's historical_data (same as FieldsSelector)
      var wpSamples =
        field.historical_data && field.historical_data.wp_samples
          ? field.historical_data.wp_samples
          : undefined;
      var fieldTimeZone =
        field.geo_data && field.geo_data.time_zone
          ? field.geo_data.time_zone
          : undefined;

      const irrigationSystem = field.irrigation_system || null;
      const irrigationRate =
        parseFloat(field.irrigation_system?.irrigation_rate) || 0;

      let lastIrrigationAmount = null;
      let lastIrrigationAmountUnit = null;
      const wfrLength = field.historical_data?.wfr_length;
      if (wfrLength?.values) {
        const wfrKeys = Object.keys(wfrLength.values).sort();
        if (wfrKeys.length > 0) {
          const latestKey = wfrKeys[wfrKeys.length - 1];
          const latestEvent = wfrLength.values[latestKey];
          if (latestEvent?.value !== undefined && latestEvent?.value !== null) {
            const parsed = parseFloat(latestEvent.value);
            if (!isNaN(parsed)) {
              lastIrrigationAmount = parsed;
            }
          }
        }
        lastIrrigationAmountUnit = wfrLength.units || null;
      }

      return {
        id: fieldId,
        name: field.name || t('unknown_field'),
        cropType: cropType,
        waterAvailability: stressLevelDisplay,
        waterAvailabilityLevel: stressLevel,
        supportsWeeklyPlanner: supportsWeeklyPlanner,
        units: recUnit,
        weeklyRecommendation: {
          amount: recAmount,
          unit: recUnit,
          timesPerWeek: recIntervalDays,
          frequencyDays: recIntervalDays,
          mode: plannerMode,
        },
        weeklyPerformance: weeklyPerformance,
        weeklyPerformanceUnits: resolveWeeklyPerformanceUnits(
          apiFieldData?.weeklyPerformanceUnits,
          userData?.length,
        ),
        weeklyGoalUnits:
          apiFieldData && apiFieldData.weeklyGoalUnits
            ? apiFieldData.weeklyGoalUnits
            : null,
        irrigationActivity: irrigationActivity,
        irrigationSystem: irrigationSystem,
        irrigationRate: irrigationRate,
        lastIrrigationAmount: lastIrrigationAmount,
        lastIrrigationAmountUnit: lastIrrigationAmountUnit,
        wpSamples: wpSamples,
        timeZone: fieldTimeZone,
      };
    });
  };

  const fieldsData = prepareFieldsData();

  const sortOptions = useMemo(
    () => [
      {
        id: 'crop_availability_low_high',
        label: t?.('sort_crop_availability') ?? 'Crop water availability',
        analyticsLabel: 'Crop water availability',
      },
      {
        id: 'name_asc',
        label: t?.('field_name') ?? 'Field Name',
        analyticsLabel: 'Field name',
      },
      {
        id: 'last_irrigation_new_old',
        label:
          t?.('sort_last_irrigation_new_old') ?? 'Last irrigation (New → Old)',
        analyticsLabel: 'Last irrigation (new -> old)',
      },
      {
        id: 'last_irrigation_old_new',
        label:
          t?.('sort_last_irrigation_old_new') ?? 'Last irrigation (Old → New)',
        analyticsLabel: 'Last irrigation (old -> new)',
      },
      {
        id: 'weekly_performance_high_low',
        label:
          t?.('sort_weekly_performance_high_low') ??
          'Weekly performance (High → Low)',
        analyticsLabel: 'Weekly performance (high -> low)',
      },
      {
        id: 'weekly_performance_low_high',
        label:
          t?.('sort_weekly_performance_low_high') ??
          'Weekly performance (Low → High)',
        analyticsLabel: 'Weekly performance (low -> high)',
      },
    ],
    [t],
  );

  const sortedFieldsData = useMemo(() => {
    const parseTimestamp = (ts) => {
      if (ts === null || ts === undefined) {
        return -1;
      }

      if (typeof ts === 'number') {
        return Number.isFinite(ts) ? ts : -1;
      }

      if (typeof ts === 'string') {
        const trimmed = ts.trim();
        if (trimmed.length === 0) {
          return -1;
        }

        // Numeric timestamps (seconds or ms) sometimes come as strings
        const numeric = Number(trimmed);
        if (Number.isFinite(numeric)) {
          return numeric;
        }

        // ISO timestamp
        const parsed = Date.parse(trimmed);
        return Number.isFinite(parsed) ? parsed : -1;
      }

      return -1;
    };

    const availabilityRank = (level) => {
      const levelStr = typeof level === 'string' ? level : String(level ?? '');
      switch (levelStr.toLowerCase()) {
        case 'low':
          return 0;
        case 'medium':
          return 1;
        case 'high':
          return 2;
        default:
          return 999;
      }
    };

    const byNameAsc = (a, b) =>
      (a?.name ?? '').localeCompare(b?.name ?? '', undefined, {
        sensitivity: 'base',
      });
    const byNameDesc = (a, b) => byNameAsc(b, a);

    // Final tiebreaker by name keeps order stable across edits/refreshes
    const withNameTiebreak = (cmp) => (a, b) => cmp(a, b) || byNameAsc(a, b);

    const byAvailLowHigh = withNameTiebreak(
      (a, b) =>
        availabilityRank(a?.waterAvailabilityLevel) -
        availabilityRank(b?.waterAvailabilityLevel),
    );
    const byAvailHighLow = withNameTiebreak(
      (a, b) =>
        availabilityRank(b?.waterAvailabilityLevel) -
        availabilityRank(a?.waterAvailabilityLevel),
    );

    const irrigationTs = (field) => {
      const ia = field?.irrigationActivity;
      return parseTimestamp(ia?.lastStartedAt ?? null);
    };
    const byIrrigationNewOld = withNameTiebreak(
      (a, b) => irrigationTs(b) - irrigationTs(a),
    );
    const byIrrigationOldNew = withNameTiebreak(
      (a, b) => irrigationTs(a) - irrigationTs(b),
    );

    const weeklyPerf = (field) => {
      const val = field?.weeklyPerformance?.value;
      if (val === null || val === undefined) {
        return -1;
      }
      const n = Number(val);
      return Number.isFinite(n) ? n : -1;
    };
    const byWeeklyPerfHighLow = withNameTiebreak(
      (a, b) => weeklyPerf(b) - weeklyPerf(a),
    );
    const byWeeklyPerfLowHigh = withNameTiebreak(
      (a, b) => weeklyPerf(a) - weeklyPerf(b),
    );

    if (!fieldsData || fieldsData.length === 0) {
      return [];
    }

    const sorted = [...fieldsData];

    switch (sortOption) {
      case 'name_desc':
        sorted.sort(byNameDesc);
        break;
      case 'crop_availability_low_high':
        sorted.sort(byAvailLowHigh);
        break;
      case 'crop_availability_high_low':
        sorted.sort(byAvailHighLow);
        break;
      case 'last_irrigation_new_old':
        sorted.sort(byIrrigationNewOld);
        break;
      case 'last_irrigation_old_new':
        sorted.sort(byIrrigationOldNew);
        break;
      case 'weekly_performance_high_low':
        sorted.sort(byWeeklyPerfHighLow);
        break;
      case 'weekly_performance_low_high':
        sorted.sort(byWeeklyPerfLowHigh);
        break;
      case 'name_asc':
      default:
        sorted.sort(byNameAsc);
        break;
    }

    return sorted;
  }, [fieldsData, sortOption]);

  useEffect(() => {
    if (!isSortMenuOpen) {
      return undefined;
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsSortMenuOpen(false);
      }
    };

    const handlePointerDown = (e) => {
      const buttonEl = sortButtonRef.current;
      const menuEl = sortMenuRef.current;
      const target = e.target;

      if (
        !target ||
        (buttonEl && buttonEl.contains(target)) ||
        (menuEl && menuEl.contains(target))
      ) {
        return;
      }

      setIsSortMenuOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isSortMenuOpen, sortButtonRef, sortMenuRef]);

  // Arrow key navigation for field list
  useEffect(() => {
    const handleFieldNavigation = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Escape')
        return;

      if (e.key === 'Escape') {
        if (selectedFieldId !== null) {
          deselectField(selectedFieldId, 'keyboard', true);
        }
        return;
      }

      if (!sortedFieldsData || sortedFieldsData.length === 0) return;

      e.preventDefault();

      const currentIndex = sortedFieldsData.findIndex(
        (field) => field.id === selectedFieldId,
      );

      let nextIndex;
      if (e.key === 'ArrowDown') {
        nextIndex =
          currentIndex === -1
            ? 0
            : Math.min(currentIndex + 1, sortedFieldsData.length - 1);
      } else {
        nextIndex =
          currentIndex === -1
            ? sortedFieldsData.length - 1
            : Math.max(currentIndex - 1, 0);
      }

      const nextField = sortedFieldsData[nextIndex];
      if (nextField && nextField.id !== selectedFieldId) {
        selectField(nextField, 'keyboard');
        requestAnimationFrame(() => {
          document
            .querySelector(`[data-field-id="${nextField.id}"]`)
            ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
      }
    };

    document.addEventListener('keydown', handleFieldNavigation);
    return () => {
      document.removeEventListener('keydown', handleFieldNavigation);
    };
  }, [sortedFieldsData, selectedFieldId]);

  // Keep a field selected so the graph and the map highlight are visible
  // instead of the empty "select a field" placeholder. Single-field users get
  // their only field selected on BOTH the map and Details tabs; multi-field
  // users get the first field auto-selected on the Details tab only (the map
  // keeps its overview). Runs whenever the selection is missing/stale for the
  // current unit (covers async field loading and switching distribution /
  // farming unit); a suppress flag preserves an intentional deselect until the
  // context refreshes. See autoSelectFieldHelpers.js.
  useEffect(() => {
    const enteredDetailsTab =
      trailingTab === 'details' && prevTrailingTabRef.current !== 'details';
    const farmingUnitChanged =
      prevFarmingUnitIdRef.current !== null &&
      prevFarmingUnitIdRef.current !== farmingUnitId;
    prevTrailingTabRef.current = trailingTab;
    prevFarmingUnitIdRef.current = farmingUnitId;

    // A fresh context clears an earlier deliberate deselect.
    if (isFreshDetailsContext({ enteredDetailsTab, farmingUnitChanged })) {
      suppressAutoSelectRef.current = false;
    }

    const hasValidSelection = hasValidFieldSelection(
      selectedFieldId,
      sortedFieldsData,
    );
    const firstField = sortedFieldsData[0];
    const isFirstFieldSelected = selectedFieldId === firstField?.id;
    const commonArgs = {
      trailingTab,
      suppressed: suppressAutoSelectRef.current,
      hasValidSelection,
      fieldCount: sortedFieldsData.length,
    };

    if (
      shouldAutoSelectFirstField(commonArgs) ||
      // The sort settles after fields load (availability/performance stream in),
      // so keep an auto-selection on the actual sorted-first field until the
      // user manually picks one.
      shouldRealignAutoSelection({
        ...commonArgs,
        wasAutoSelected: autoSelectedRef.current,
        isFirstFieldSelected,
      })
    ) {
      selectField(firstField, 'auto');
    }
    // selectField is recreated each render; the guards above make this a no-op
    // except when the sorted-first field genuinely needs (re)selecting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trailingTab, selectedFieldId, farmingUnitId, sortedFieldsData]);

  // Keyboard shortcuts: 'a' for advanced mode, 'n' for NDVI toggle
  useEffect(() => {
    const handleShortcuts = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      if (e.key === 'a' || e.key === 'A') {
        handleAdvancedToggle();
      } else if (e.key === 'n' || e.key === 'N') {
        if (customMapInstance) {
          customMapInstance.changeNDVIToggle();
        }
        setShowNDVI((prev) => {
          const ndviOn = !prev;
          trackEvent('Farm View NDVI Toggled', { enabled: ndviOn });
          return ndviOn;
        });
      }
    };

    document.addEventListener('keydown', handleShortcuts);
    return () => {
      document.removeEventListener('keydown', handleShortcuts);
    };
  }, [handleAdvancedToggle, trackEvent, customMapInstance]);

  const handleSortOptionSelect = (newSortOption) => {
    if (newSortOption === sortOption) {
      setIsSortMenuOpen(false);
      return;
    }

    var selectedSortOption = sortOptions.find(
      (opt) => opt.id === newSortOption,
    );
    setSortOption(newSortOption);
    setIsSortMenuOpen(false);

    trackEvent('Farm View Sort Change', {
      'Sort Type': selectedSortOption?.analyticsLabel || newSortOption,
    });
  };

  const handleSortMenuToggle = () => {
    setIsSortMenuOpen((wasOpen) => {
      if (!wasOpen) {
        trackEvent('Farm View Sort Menu Loaded');
      }
      return !wasOpen;
    });
  };

  // Prepare map data for all fields
  const [mapAreas, setMapAreas] = useState({});
  const [mapMarkers, setMapMarkers] = useState({});

  // Signature of the last map-relevant state we actually repainted. A single
  // field selection re-renders 3–5 times in quick succession (local id state,
  // the Redux selected_entities dispatch, field-history init→success, NDVI
  // arrival giving farmingUnitForMap a new reference). Repainting on every tick
  // re-applies the base layer and reloads the satellite tiles, so the whole map
  // blinks once per tick. We repaint only when the signature actually changes.
  const lastMapSignatureRef = useRef(null);
  // Previous trailing tab as seen by THIS effect (the auto-select effect owns
  // its own prevTrailingTabRef and updates it earlier in the same render, so we
  // cannot share it here).
  const prevMapTabRef = useRef(trailingTab);

  // Update map when fields or selection changes.
  // Skip while the Field details tab is showing — the map container is
  // hidden via display:none, so its size is 0×0 and Leaflet's projection
  // produces NaN inside flyToBounds. When the user toggles back to the
  // Farm map tab we recalc size and re-run the update.
  useEffect(() => {
    const tabJustReturned =
      trailingTab === 'farm' && prevMapTabRef.current !== 'farm';
    prevMapTabRef.current = trailingTab;

    if (trailingTab !== 'farm') {
      // still prepare the data so it's ready when the user comes back
      if (farmingUnitForMap) {
        prepareMapData();
      }
      return;
    }
    if (customMapInstance && farmingUnitForMap) {
      // A repaint reprojects the map (invalidateSize) and re-fits bounds, so
      // only do it when something the map draws has actually changed. This
      // collapses the multi-render cascade of a single selection into one
      // repaint and stops the flicker. Force a repaint when the tab was just
      // switched back to the map (signature already matches but the hidden
      // container needs a resize + redraw).
      const signature = buildMapRepaintSignature({
        farmingUnitId: farmingUnitForMap.id,
        selectedFieldId,
        archiveMode,
        hasFieldNdvi: Boolean(
          selectedFieldId &&
          farmingUnitForMap.fields?.[selectedFieldId]?.historical_data?.ndvi,
        ),
        hasFarmingUnitNdvi: Boolean(farmingUnitForMap.historical_data?.ndvi),
        fieldHistoryStatus:
          fieldLoadHistoryStatus && fieldLoadHistoryStatus.status,
        farmingUnitHistoryStatus: loadHistoryStatus && loadHistoryStatus.status,
      });
      if (signature === lastMapSignatureRef.current && !tabJustReturned) {
        return;
      }
      lastMapSignatureRef.current = signature;
      if (customMapInstance.mymap) {
        try {
          customMapInstance.mymap.invalidateSize();
        } catch (e) {
          /* leaflet not ready, refresh below will paint anyway */
        }
      }
      updateMap();
    } else if (farmingUnitForMap) {
      // Prepare data even before map is ready
      prepareMapData();
    }
    // Deps are intentionally broad — the signature above is the real gate for
    // an (expensive) repaint, so extra re-runs from these are cheap no-ops (an
    // O(1) string compare that returns early). `selectedField` and
    // `historyStatusUpdatedAt` are kept as a safety net: they re-run the effect
    // when Redux mutates historical_data in place, so the signature is
    // re-checked even if `farmingUnitForMap`'s reference happens not to change.
    // Do not trim them to "match the body" — that would remove that safety net
    // without removing any repaint work.
  }, [
    selectedFieldId,
    farmingUnitForMap,
    selectedField,
    archiveMode,
    customMapInstance,
    trailingTab,
    loadHistoryStatus && loadHistoryStatus.status,
    fieldLoadHistoryStatus && fieldLoadHistoryStatus.status,
    historyStatusUpdatedAt,
  ]);

  const prepareMapData = () => {
    if (!farmingUnitForMap) return;

    const fieldsState = archiveMode ? 'archive' : 'active';

    // Get ALL fields from the farming unit
    let all_areas = farmingUnitManager.all_farming_unit_areas(
      farmingUnitForMap,
      '', // Show all fields
      fieldsState,
    );

    // Apply colors to field polygons
    const fieldsData = prepareFieldsData();
    // Convert to array preserving IDs from object keys
    let areasArray;
    if (Array.isArray(all_areas)) {
      areasArray = all_areas;
    } else {
      areasArray = Object.entries(all_areas || {}).map(([id, area]) => ({
        ...area,
        id: id,
      }));
    }

    const coloredAreas = areasArray.map((area) => {
      const fieldData = fieldsData.find((f) => f.id === area.id);

      if (fieldData) {
        let strokeColor = '#DCE0E8';
        let fillColor = 'transparent';
        let fillOpacity = 0;

        switch (fieldData.waterAvailabilityLevel?.toLowerCase()) {
          case 'low':
            strokeColor = '#DF3044';
            fillColor = '#DF3044';
            fillOpacity = 0.1;
            break;
          case 'medium':
            strokeColor = '#FFB302';
            fillColor = '#FFB302';
            fillOpacity = 0.1;
            break;
          case 'high':
            strokeColor = '#078378';
            fillColor = '#078378';
            fillOpacity = 0.1;
            break;
        }

        return {
          ...area,
          strokeColor: strokeColor,
          strokeWeight: 3,
          strokeOpacity: 1.0,
          fillColor: fillColor,
          fillOpacity: fillOpacity,
        };
      }

      return area;
    });

    const finalAreas = Array.isArray(all_areas)
      ? coloredAreas
      : coloredAreas.reduce((obj, area) => {
          obj[area.id] = area;
          return obj;
        }, {});

    let all_markers = {};
    if (
      farmingUnitForMap &&
      farmingUnitManager.shouldDisplayAllSensors(farmingUnitForMap)
    ) {
      all_markers = farmingUnitManager.all_farming_unit_markers(
        farmingUnitForMap,
        selectedFieldId || '',
        fieldsState,
      );
    }

    setMapAreas(finalAreas);
    setMapMarkers(all_markers);
  };

  const updateMap = () => {
    // No `fields` means the farming unit is still a skeleton (its full
    // distribution tree hasn't loaded yet); the area/field builders would
    // dereference undefined fields/sensors. Bail until the full tree lands.
    if (!customMapInstance || !farmingUnitForMap || !farmingUnitForMap.fields) {
      return;
    }

    const fieldsState = archiveMode ? 'archive' : 'active';

    // Get ALL fields from the farming unit (pass empty string to show all)
    let all_areas = farmingUnitManager.all_farming_unit_areas(
      farmingUnitForMap,
      '', // Show all fields, not just selected one
      fieldsState,
    );

    // Apply colors to field polygons based on water availability status
    const fieldsData = prepareFieldsData();

    // Convert to array if it's an object - preserve IDs using Object.entries
    let areasArray;
    if (Array.isArray(all_areas)) {
      areasArray = all_areas;
    } else {
      // Object.entries gives us [key, value] pairs, so we add the id to each area
      areasArray = Object.entries(all_areas || {}).map(([id, area]) => ({
        ...area,
        id: id, // Ensure id is set from the object key
      }));
    }

    const coloredAreas = areasArray.map((area) => {
      // Find matching field data
      const fieldData = fieldsData.find((f) => f.id === area.id);
      const isSelected = area.id === selectedFieldId;

      if (fieldData) {
        // Determine color based on water availability level
        let strokeColor = '#DCE0E8'; // Default grey
        let fillColor = 'transparent'; // Default no fill
        let fillOpacity = 0;

        switch (fieldData.waterAvailabilityLevel?.toLowerCase()) {
          case 'low':
            strokeColor = '#DF3044'; // Red
            fillColor = '#DF3044';
            fillOpacity = isSelected ? 0.5 : 0.3;
            break;
          case 'medium':
            strokeColor = '#FFB302'; // Orange/Yellow
            fillColor = '#FFB302';
            fillOpacity = isSelected ? 0.5 : 0.3;
            break;
          case 'high':
            strokeColor = '#078378'; // Green
            fillColor = '#078378';
            fillOpacity = isSelected ? 0.5 : 0.3;
            break;
        }

        // Apply the stroke and fill color to the area
        // Mark as selected to trigger zoom in CustomMap
        return {
          ...area,
          selected: isSelected,
          fill_color: fillColor,
          strokeColor: strokeColor,
          strokeWeight: isSelected ? 4 : 3,
          strokeOpacity: 1.0,
          fillColor: fillColor,
          fillOpacity: fillOpacity,
        };
      }

      return { ...area, selected: isSelected };
    });

    // Convert back to object if original was an object
    const finalAreas = Array.isArray(all_areas)
      ? coloredAreas
      : coloredAreas.reduce((obj, area) => {
          obj[area.id] = area;
          return obj;
        }, {});

    let all_markers = {};
    if (
      farmingUnitForMap &&
      farmingUnitManager.shouldDisplayAllSensors(farmingUnitForMap)
    ) {
      all_markers = farmingUnitManager.all_farming_unit_markers(
        farmingUnitForMap,
        selectedFieldId || '',
        fieldsState,
      );
    }

    // Build NDVI polygons for farm view
    // No field selected → farming unit level NDVI (all polygons)
    // Field selected → selected field's NDVI only
    const ndviPolygons = [{}];
    let shouldPresentFarmingUnitNdvi = false;
    const hasFieldSelected = selectedFieldId !== null;

    // Get farming unit level NDVI data
    const farmingUnitNdviValues =
      farmingUnitForMap.historical_data?.ndvi?.values ?? {};
    // Field tiles are keyed by the field capture timestamp, which can differ
    // from the farming-unit capture timestamp by a few minutes on the same day.
    // Index the farming-unit images by day so the join still finds them.
    const farmingUnitNdviDayIndex = buildFarmingUnitNdviDayIndex(
      farmingUnitNdviValues,
    );

    // Track which dates already have a reference tile (for color bar min/max)
    const usedReferenceDate = {};

    for (const [fieldId, currentField] of Object.entries(
      farmingUnitForMap.fields,
    )) {
      const isSelectedField = fieldId === selectedFieldId;

      if (
        currentField.historical_data &&
        'ndvi' in currentField.historical_data
      ) {
        const currentNDVITiles =
          currentField.historical_data['ndvi']['values'] || {};
        const fieldLastCaptureDate = latestKeyfromDict(currentNDVITiles);

        if (
          getFarmingUnitNdviForTimestamp(
            farmingUnitNdviValues,
            farmingUnitNdviDayIndex,
            fieldLastCaptureDate,
          )
        ) {
          shouldPresentFarmingUnitNdvi = true;
        }

        for (const key in currentNDVITiles) {
          let currentValue = { ...currentNDVITiles[key] };
          const farmingUnitNdvi = getFarmingUnitNdviForTimestamp(
            farmingUnitNdviValues,
            farmingUnitNdviDayIndex,
            key,
          );

          if (currentValue['image_path']) {
            if (hasFieldSelected) {
              // Field selected: mark only the selected field's tiles
              currentValue['isSelectedField'] = isSelectedField;
              if (isSelectedField && farmingUnitNdvi) {
                currentValue['farming_details'] = farmingUnitNdvi;
              }
            } else {
              // No field selected: mark the first tile per day as reference.
              // Keyed by day (not exact timestamp) so a day whose fields were
              // captured seconds apart gets a single reference tile.
              const dayKey = getNdviDayKey(key);
              const isReference = !usedReferenceDate[dayKey];
              currentValue['isSelectedField'] = isReference;
              if (isReference) {
                usedReferenceDate[dayKey] = true;
              }
              if (isReference && farmingUnitNdvi) {
                currentValue['farming_details'] = farmingUnitNdvi;
              }
            }
          } else {
            currentValue = {
              tile: currentValue,
              mean: undefined,
              cloudiness: undefined,
              useful_area: undefined,
              source: undefined,
            };
          }

          if (key in ndviPolygons[0]) {
            ndviPolygons[0][key].push(currentValue);
          } else {
            ndviPolygons[0][key] = [currentValue];
          }
        }
      }
    }

    // Fields of a farming unit are captured seconds apart in the same satellite
    // pass, so their tiles land under near-but-not-equal timestamps. Collapse to
    // one entry per day so the timeline shows a single row per date and each row
    // holds every field tile (and the farming-unit image) captured that day.
    ndviPolygons[0] = collapseNdviTilesByDay(ndviPolygons[0]);

    // Tell the map which farming unit it is showing so it re-fits the viewport
    // when the user switches farming units, even with no field selected (N-22).
    customMapInstance.setCurrentFarmingUnitId(farmingUnitForMap.id);

    // Use invalidate with onComplete callback to sync layer selection after map is ready
    customMapInstance.invalidate(
      finalAreas,
      all_markers,
      ndviPolygons,
      false,
      shouldPresentFarmingUnitNdvi,
      function () {
        // Map is now fully updated - sync layer selection with NDVI state.
        // Read NDVI state directly from the map instance (not React state)
        // to avoid stale closure when triggered by field selection changes.
        const isNdviOn = !customMapInstance.getNdviToggleState();
        // With NDVI ON the overlay was reset during refresh() and must be
        // re-applied. With NDVI OFF, refresh() left the base tile layer
        // untouched, so re-selecting 'Satelite' would remove+re-add the
        // satellite tiles and blink the whole map — skip it.
        if (isNdviOn) {
          customMapInstance.onLayerSelection({ target: { id: 'ndvi' } });
        }
      },
    );
  };

  // Handle archive action (values passed from ApprovalModal)
  const handleArchiveField = (values) => {
    if (values && values.length === 3) {
      var distId = values[0];
      var fuId = values[1];
      var fId = values[2];
      // Get the field object from selectedFarmingUnit
      var field = selectedFarmingUnit?.fields?.[fId];
      if (field) {
        updateField(distId, fuId, {
          ...field,
          settings: {
            ...field.settings,
            field_state: 'archive',
          },
        });
      }
    }
  };

  // Handle delete action (values passed from ApprovalModal)
  const handleDeleteFieldAction = (values) => {
    if (values && values.length === 3) {
      var distId = values[0];
      var fuId = values[1];
      var fId = values[2];
      deleteField(distId, fuId, fId);
    }
  };

  return (
    <div className='irrigation-monitoring'>
      {/* Modals */}
      <UpdateFieldModal key={selectedFieldId} modalId='fieldModalId' />
      <ApprovalModal
        modalId='approvalModalId'
        message={
          t('archive_field_confirmation') ||
          'Are you sure you want to archive this field?'
        }
        onApprove={handleArchiveField}
      />
      <ApprovalModal
        modalId='deletionApprovalModalId'
        message={
          t('edit_field_delete_field_message') ||
          'Are you sure you want to delete this field?'
        }
        onApprove={handleDeleteFieldAction}
      />
      <ReportIssueModal modalId='reportIssueModalId' />

      {/* Left Panel - Field List */}
      <div className={`leading-panel ${isAdvancedMode ? 'advanced' : 'basic'}`}>
        {/* Header with Sort and Advanced/Close buttons */}
        <div className='selectors'>
          {/* Sort button */}
          <div className='all-farm-sort'>
            <button
              ref={sortButtonRef}
              type='button'
              className={`all-farm-sort__button ${isSortMenuOpen ? 'all-farm-sort__button--open' : ''}`}
              aria-haspopup='menu'
              aria-expanded={isSortMenuOpen}
              aria-controls='all-farm-sort-menu'
              onClick={handleSortMenuToggle}
            >
              <svg
                width='16'
                height='14'
                viewBox='0 0 16 14'
                fill='none'
                className='selector-icon'
                aria-hidden='true'
              >
                <path
                  d='M12.7484 0.336693C12.5586 0.122568 12.2862 0 12 0C11.7139 0 11.4415 0.122568 11.2517 0.336693L8.25169 3.72131C7.88535 4.13461 7.92343 4.76663 8.33673 5.13296C8.75003 5.4993 9.38205 5.46122 9.74838 5.04792L11 3.6358V12C11 12.5523 11.4478 13 12 13C12.5523 13 13 12.5523 13 12V3.6358L14.2517 5.04792C14.618 5.46122 15.25 5.4993 15.6633 5.13296C16.0766 4.76663 16.1147 4.13461 15.7484 3.72131L12.7484 0.336693Z'
                  fill='currentColor'
                />
                <path
                  d='M3.25166 13.6633C3.44146 13.8774 3.71388 14 4.00001 14C4.28614 14 4.55857 13.8774 4.74836 13.6633L7.74836 10.2787C8.11469 9.86539 8.07662 9.23337 7.66332 8.86704C7.25002 8.5007 6.618 8.53878 6.25167 8.95208L5.00001 10.3642L5.00001 2C5.00001 1.44772 4.5523 1 4.00001 1C3.44773 1 3.00001 1.44772 3.00001 2L3.00001 10.3642L1.74836 8.95208C1.38202 8.53878 0.750004 8.5007 0.336704 8.86704C-0.0765968 9.23337 -0.11467 9.86539 0.251664 10.2787L3.25166 13.6633Z'
                  fill='currentColor'
                />
              </svg>
              <span className='text-style-short-lables-16-semi-bold'>
                {t('sort') || 'Sort'}
              </span>
            </button>

            {isSortMenuOpen && (
              <div
                ref={sortMenuRef}
                id='all-farm-sort-menu'
                className='all-farm-sort__menu'
                role='menu'
              >
                {sortOptions.map((opt) => {
                  const isSelected = opt.id === sortOption;
                  return (
                    <button
                      key={opt.id}
                      type='button'
                      className='all-farm-sort__menu-item'
                      role='menuitemradio'
                      aria-checked={isSelected}
                      onClick={() => handleSortOptionSelect(opt.id)}
                    >
                      <span className='all-farm-sort__menu-item-left'>
                        <span
                          className={`all-farm-sort__menu-check ${isSelected ? 'all-farm-sort__menu-check--visible' : ''}`}
                          aria-hidden='true'
                        >
                          <img
                            src={require('images/icons/checkmark.svg')}
                            alt=''
                            width='10'
                            height='9'
                          />
                        </span>
                        <span className='all-farm-sort__menu-item-label text-style-short-lables-16-regular'>
                          {opt.label}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Advanced/Close button */}
          <button
            className={`button ${isAdvancedMode ? 'expanded' : 'minimized'}`}
            onClick={handleAdvancedToggle}
          >
            {isAdvancedMode ? (
              <>
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                  <path
                    d='M12 4L4 12M4 4l8 8'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                  />
                </svg>
                <div>{t('close') || 'Close'}</div>
              </>
            ) : (
              <>
                <div>{t('advanced') || 'Advanced'}</div>
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                  <path
                    d='M6 11l4-4-4-4'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </>
            )}
          </button>
        </div>

        {/* Table */}
        <div className='fields-table'>
          {/* Scroll wrapper for horizontal scrolling in advanced mode */}
          <div className='fields-table-scroll'>
            {/* Table header */}
            <TableHeader isAdvanced={isAdvancedMode} t={t} />

            {/* Field list */}
            <div className='field-list'>
              {sortedFieldsData.map((field) => (
                <FieldRow
                  key={field.id}
                  field={field}
                  onRowClick={handleFieldSelection}
                  onAlertClick={() => {
                    trackEvent('Farm View Alert Clicked', {
                      field_id: field.id,
                    });
                  }}
                  userData={userData}
                  isSelected={selectedFieldId === field.id}
                  fieldNotifications={fieldNotificationsMap[field.id] || []}
                  isAdvanced={isAdvancedMode}
                  isHovered={hoveredFieldId === field.id}
                  onMouseEnter={() => setHoveredFieldId(field.id)}
                  onMouseLeave={() => setHoveredFieldId(null)}
                  settingsMenu={renderFieldSettingsMenu(field)}
                  isMenuOpen={openMenuFieldId === field.id}
                  onMenuToggle={(isOpen, iconElement) => {
                    if (isOpen && iconElement) {
                      var rect = iconElement.getBoundingClientRect();
                      var menuWidth = 180;
                      var menuHeight = SETTINGS_MENU_ESTIMATED_HEIGHT;
                      var margin = 8;

                      // Calculate horizontal position
                      var left = rect.right - menuWidth;
                      if (left < margin) {
                        left = margin;
                      }
                      if (left + menuWidth > window.innerWidth - margin) {
                        left = window.innerWidth - menuWidth - margin;
                      }

                      // Calculate vertical position - check if menu fits below
                      var spaceBelow =
                        window.innerHeight - rect.bottom - margin;
                      var spaceAbove = rect.top - margin;
                      var openAbove =
                        spaceBelow < menuHeight && spaceAbove > spaceBelow;

                      var top;
                      if (openAbove) {
                        // Position above the icon
                        top = rect.top - menuHeight - SETTINGS_MENU_GAP;
                        // Ensure menu doesn't go above viewport
                        if (top < margin) {
                          top = margin;
                        }
                      } else {
                        // Position below the icon
                        top = rect.bottom + SETTINGS_MENU_GAP;
                      }

                      setMenuPosition({
                        top: top,
                        left: left,
                      });
                    } else {
                      setMenuPosition(null);
                    }
                    setOpenMenuFieldId(isOpen ? field.id : null);
                  }}
                  menuPosition={
                    openMenuFieldId === field.id ? menuPosition : null
                  }
                  t={t}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Map / Field details */}
      <div className='trailing-panel-wrap'>
        <div
          className='trailing-panel__tabs'
          role='tablist'
          aria-label={t('farm_map')}
        >
          <button
            type='button'
            role='tab'
            aria-selected={trailingTab === 'farm'}
            className={`trailing-panel__tab ${
              trailingTab === 'farm' ? 'trailing-panel__tab--selected' : ''
            }`}
            onClick={() => setTrailingTab('farm')}
          >
            {t('farm_map')}
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={trailingTab === 'details'}
            className={`trailing-panel__tab ${
              trailingTab === 'details' ? 'trailing-panel__tab--selected' : ''
            }`}
            onClick={() => setTrailingTab('details')}
          >
            {t('field_details')}
          </button>
        </div>
        <div className='trailing-panel'>
          {/* Map stays mounted to avoid Leaflet teardown; toggle visibility */}
          <div
            className={`map-container ${showNDVI ? 'ndvi-on' : 'ndvi-off'}`}
            style={{ display: trailingTab === 'farm' ? 'flex' : 'none' }}
          >
            <CustomMap
              onRef={(ref) => setCustomMapInstance(ref)}
              mapId='irrigation-monitoring-map'
              showEditToolBar={false}
              message=''
              onPolygonClick={handlePolygonClick}
              onMarkerClick={handleMarkerClick}
              onLayerSelection={handleLayerSelection}
              onNDVILayerSelection={() => {}}
              map_height={window.innerHeight - 200}
              shouldPresentStressMenu={false}
              shouldPresentFilePicker={false}
              shouldDisplayNDVI={true}
              defaultNdviOff={true}
              selectedFieldOnly={selectedFieldId !== null}
              hideSelectedFieldToggle={true}
              all_areas={mapAreas}
              all_markers={mapMarkers}
            />
          </div>
          {/* Details panel is always mounted (when a field is selected)
              so sensor sample fetches start the moment the user clicks
              a field, in parallel with the field-history HTTP. We just
              hide it when the Farm map tab is showing. */}
          <div
            className='trailing-panel__details'
            style={{ display: trailingTab === 'details' ? 'flex' : 'none' }}
          >
            {selectedFieldId && selectedField?.id === selectedFieldId ? (
              <FieldDetailsWPGraph />
            ) : (
              <div className='trailing-panel__details-empty'>
                {t('select_a_field_to_view_details')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

IrrigationSystemMonitoring.propTypes = {
  selectedFarmingUnit: PropTypes.object,
  selectedDistribution: PropTypes.object,
  selectedField: PropTypes.object,
  plannerType: PropTypes.string,
  archiveMode: PropTypes.bool,
  userDetails: PropTypes.object,
  farmingUnitRecommendations: PropTypes.object,
  loadHistoryStatus: PropTypes.object,
  fieldLoadHistoryStatus: PropTypes.object,
  distributions: PropTypes.object,
  loadFarmingUnitHistory: PropTypes.func,
  configurationData: PropTypes.object,
  t: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  selectedFarmingUnit: state.distribution_data.selected_entities.farming_unit,
  selectedDistribution: state.distribution_data.selected_entities.distribution,
  selectedField: state.distribution_data.selected_entities.field,
  plannerType: state.distribution_data.planner_type,
  archiveMode: state.distribution_data.archive_mode,
  userNotifications: state.authentication_data.user_notifications,
  userDetails: state.authentication_data.user_details,
  userData: state.authentication_data.user_data,
  farmingUnitRecommendations:
    state.distribution_data.farming_unit_recommendations?.recommendations || {},
  // Subscribe to loadHistoryStatus to trigger re-render when historical_data (wp_samples) is loaded
  loadHistoryStatus: state.distribution_data.farming_unit_load_history,
  fieldLoadHistoryStatus: state.distribution_data.field_load_history,
  distributions: state.distribution_data.distributions?.distributions,
  // Remote configuration data for feature flags
  configurationData: state.configuration_data,
  farmingUnitRoles: state.roles_data?.roles?.selected_farming_unit_roles,
  // Field create/update/delete outcome — used to re-pull the farm view data
  // (weekly performance / goal) as soon as a field is saved.
  fieldActions: state.distribution_data.field_actions,
});

var mapDispatchToProps = function (dispatch) {
  return {
    loadFarmingUnitHistory: function (distributionId, farmingUnitId, fieldId) {
      dispatch(
        distributionActions.get_farming_units_history(
          distributionId,
          farmingUnitId,
          fieldId,
        ),
      );
    },
    deleteField: function (distributionId, farmingUnitId, fieldId) {
      dispatch(
        distributionActions.delete_field(
          distributionId,
          farmingUnitId,
          fieldId,
        ),
      );
    },
    updateField: function (distributionId, farmingUnitId, fieldData) {
      dispatch(
        distributionActions.update_field(
          distributionId,
          farmingUnitId,
          fieldData,
        ),
      );
    },
    loadFieldHistory: function (distributionId, farmingUnitId, fieldId) {
      dispatch(
        distributionActions.get_field_history(
          distributionId,
          farmingUnitId,
          fieldId,
        ),
      );
    },
  };
};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(IrrigationSystemMonitoring));
